import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Gamepad2, Maximize2, Info, ExternalLink, Play, LayoutGrid, List, CalendarDays } from 'lucide-react';


// Dragon Ball Flash Games - VERIFIED embed URLs from retrogames.cc
export const FLASH_GAMES = [
  // ===== DRAGON BALL Z - GBA =====
  {
    id: 'dbz-legacy-goku',
    title: 'Dragon Ball Z: Legacy of Goku',
    series: 'Dragon Ball Z',
    description: 'GBA RPG - Play as Goku from Raditz arrival through Frieza saga! Level up and master Ki attacks.',
    embedUrl: 'https://www.retrogames.cc/embed/29516-dragon-ball-z-the-legacy-of-goku-u-mode7.html',
    directUrl: 'https://www.retrogames.cc/gameboyadvance-games/dragon-ball-z-the-legacy-of-goku-u-mode7.html',
    controls: 'Arrow Keys to move, Z to attack, X for Ki blast, Enter to start',
    year: '2002'
  },
  {
    id: 'dbz-legacy-goku-2',
    title: 'Dragon Ball Z: Legacy of Goku II',
    series: 'Dragon Ball Z',
    description: 'GBA sequel covering the Android and Cell sagas! Play as Goku, Gohan, Piccolo, Vegeta, and Trunks!',
    embedUrl: 'https://www.retrogames.cc/embed/17285-dragon-ball-z-the-legacy-of-goku-ii-u-trashman.html',
    directUrl: 'https://www.retrogames.cc/gameboyadvance-games/dragon-ball-z-the-legacy-of-goku-ii-u-trashman.html',
    controls: 'Arrow Keys to move, Z to attack, X for Ki blast',
    year: '2003'
  },
  {
    id: 'dbz-buus-fury',
    title: "Dragon Ball Z: Buu's Fury",
    series: 'Dragon Ball Z',
    description: 'GBA RPG covering the entire Buu saga! Play as Goku, Gohan, Vegeta, Goten, and Trunks with fusions!',
    embedUrl: 'https://www.retrogames.cc/embed/28935-dragon-ball-z-buu-s-fury-u-psychosis.html',
    directUrl: 'https://www.retrogames.cc/gameboyadvance-games/dragon-ball-z-buu-s-fury-u-psychosis.html',
    controls: 'Arrow Keys to move, Z to attack, X for Ki blast',
    year: '2004'
  },
  {
    id: 'dbz-supersonic-warriors',
    title: 'Dragon Ball Z: Supersonic Warriors',
    series: 'Dragon Ball Z',
    description: 'GBA fighting game with aerial combat! Play as 13 characters with unique story modes!',
    embedUrl: 'https://www.retrogames.cc/embed/19537-dragonball-z-supersonic-warriors-e-rising-sun.html',
    directUrl: 'https://www.retrogames.cc/gameboyadvance-games/dragonball-z-supersonic-warriors-e-rising-sun.html',
    controls: 'Arrow Keys to move, Z to punch, X for Ki, A/S for dash',
    year: '2004'
  },
  {
    id: 'dbz-taiketsu',
    title: 'Dragon Ball Z: Taiketsu',
    series: 'Dragon Ball Z',
    description: 'GBA fighting game with 15 playable characters including Super Saiyan transformations!',
    embedUrl: 'https://www.retrogames.cc/embed/19436-dragon-ball-z-taiketsu-u-rising-sun.html',
    directUrl: 'https://www.retrogames.cc/gameboyadvance-games/dragon-ball-z-taiketsu-u-rising-sun.html',
    controls: 'Arrow Keys to move, Z/X to attack',
    year: '2003'
  },
  // ===== DRAGON BALL (Original) =====
  {
    id: 'db-advanced-adventure',
    title: 'Dragon Ball: Advanced Adventure',
    series: 'Dragon Ball',
    description: 'GBA side-scroller following young Goku through the original Dragon Ball story! Beat-em-up action!',
    embedUrl: 'https://www.retrogames.cc/embed/27802-dragon-ball-advance-adventure-j-rising-sun.html',
    directUrl: 'https://www.retrogames.cc/gameboyadvance-games/dragon-ball-advance-adventure-j-rising-sun.html',
    controls: 'Arrow Keys to move, Z to attack, X to jump',
    year: '2004'
  },
  // ===== DRAGON BALL GT =====
  {
    id: 'dbgt-transformation',
    title: 'Dragon Ball GT: Transformation',
    series: 'Dragon Ball GT',
    description: 'GBA beat-em-up featuring GT characters! Unlock SSJ4 Goku and fight through the Black Star saga!',
    embedUrl: 'https://www.retrogames.cc/embed/27773-dragon-ball-gt-transformation-u-trashman.html',
    directUrl: 'https://www.retrogames.cc/gameboyadvance-games/dragon-ball-gt-transformation-u-trashman.html',
    controls: 'Arrow Keys to move, Z to attack, X for special',
    year: '2005'
  },
  // ===== COMBO PACKS =====
  {
    id: 'dbz-legacy-combo',
    title: 'DBZ: Legacy of Goku I & II (2-in-1)',
    series: 'Dragon Ball Z',
    description: 'Both Legacy of Goku games in one! Play through Saiyan, Namek, Android, and Cell sagas!',
    embedUrl: 'https://www.retrogames.cc/embed/29379-2-in-1-dragon-ball-z-the-legacy-of-goku-i-ii-u-rising-sun.html',
    directUrl: 'https://www.retrogames.cc/gameboyadvance-games/2-in-1-dragon-ball-z-the-legacy-of-goku-i-ii-u-rising-sun.html',
    controls: 'Arrow Keys to move, Z to attack, X for Ki blast',
    year: '2004'
  }
];

const SERIES_FILTERS = ['All', 'Dragon Ball', 'Dragon Ball Z', 'Dragon Ball GT'];

export const GamesPage = () => {
  const [searchParams] = useSearchParams();
  const [selectedGame, setSelectedGame] = useState(() => FLASH_GAMES.find(g => g.id === new URLSearchParams(window.location.search).get('game')) || null);
  useEffect(() => { const game = FLASH_GAMES.find(g => g.id === searchParams.get('game')); if (game) setSelectedGame(game); }, [searchParams]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [seriesFilter, setSeriesFilter] = useState('All');
  const [isLoading, setIsLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [viewMode, setViewMode] = useState('grid');

  const toggleFullscreen = () => {
    const iframe = document.getElementById('game-iframe');
    if (!iframe) return;
    
    if (!document.fullscreenElement) {
      iframe.requestFullscreen?.() || iframe.webkitRequestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.() || document.webkitExitFullscreen?.();
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const filteredGames = seriesFilter === 'All' 
    ? FLASH_GAMES 
    : FLASH_GAMES.filter(g => g.series === seriesFilter);

  const handleGameSelect = (game) => {
    setSelectedGame(game);
    setIsPlaying(false);
    setIsLoading(false);
  };

  const launchGame = (game) => {
    setSelectedGame(game);
    setIsLoading(true);
    setIsPlaying(true);
    window.requestAnimationFrame(() => document.getElementById('games-feature')?.scrollIntoView({behavior:'smooth',block:'start'}));
  };

  const coverFor = (game) => `/images/games/${game.id}.webp`; 

  return (
    <main className="dragon-games min-h-screen pt-24 md:pt-28 pb-12 px-3 md:px-6">
      <div className="max-w-7xl mx-auto">
        <header className="dragon-games-hero text-center mb-7">
          <div className="flex items-center justify-center gap-3">
            <Gamepad2 className="w-9 h-9 md:w-12 md:h-12 dragon-games-accent" />
            <h1 className="font-heading uppercase text-3xl sm:text-4xl md:text-6xl text-white">Dragon Ball <span className="dragon-games-accent">Games</span></h1>
          </div>
          <p className="text-gray-300 mt-3 text-sm md:text-lg">Play classic Dragon Ball games in your browser!</p>
        </header>

        <nav className="dragon-games-filters mb-6" aria-label="Filter games by series">
          {SERIES_FILTERS.map(filter => <button key={filter} type="button" aria-pressed={seriesFilter===filter} onClick={()=>setSeriesFilter(filter)} className={`dragon-games-filter ${seriesFilter===filter?'active':''}`}>{filter}</button>)}
        </nav>

        <section id="games-feature" className="dragon-games-feature mb-8" aria-label="Featured game and player">
          {isPlaying && selectedGame ? (
            <div className="dragon-games-player">
              <div className="flex items-center justify-between gap-2 p-3 md:p-4 border-b border-white/10">
                <div className="min-w-0"><h2 className="font-heading text-xl md:text-2xl truncate">{selectedGame.title}</h2><p className="text-gray-400 text-xs">Game Boy Advance · {selectedGame.year}</p></div>
                <div className="flex gap-2 shrink-0">
                  <button className="dragon-games-tool" onClick={()=>setShowInfo(!showInfo)} title="Game information" aria-label="Game information"><Info size={19}/></button>
                  <button className="dragon-games-tool" onClick={toggleFullscreen} title="Fullscreen" aria-label="Fullscreen"><Maximize2 size={19}/></button>
                  <a className="dragon-games-tool" href={selectedGame.directUrl} target="_blank" rel="noopener noreferrer" title="Open game on RetroGames.cc" aria-label="Open game on RetroGames.cc"><ExternalLink size={19}/></a>
                  <button className="dragon-games-tool" onClick={()=>{setIsPlaying(false);setIsLoading(false);}} title="Close player" aria-label="Close player">✕</button>
                </div>
              </div>
              {showInfo && <div className="p-3 bg-black/50 text-sm"><p>{selectedGame.description}</p><p className="mt-2 dragon-games-accent">Controls: {selectedGame.controls}</p></div>}
              <div className="relative aspect-video bg-black">
                {isLoading && <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/90"><div className="text-center"><div className="w-10 h-10 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-3 dragon-games-spinner"/><p>Loading game...</p></div></div>}
                <iframe key={selectedGame.id} id="game-iframe" src={selectedGame.embedUrl} title={selectedGame.title} className="w-full h-full border-0" allowFullScreen allow="gamepad; autoplay" onLoad={()=>setIsLoading(false)}/>
              </div>
            </div>
          ) : (
            <div className="dragon-games-feature-content">
              <div className="dragon-games-feature-cover"><img src={coverFor(selectedGame||FLASH_GAMES[1])} alt={`${(selectedGame||FLASH_GAMES[1]).title} cover`} /></div>
              <div className="dragon-games-feature-copy">
                <p className="dragon-games-eyebrow">Featured Game</p>
                <h2 className="font-heading text-2xl sm:text-3xl md:text-5xl leading-tight">{(selectedGame||FLASH_GAMES[1]).title}</h2>
                <div className="flex flex-wrap gap-4 text-sm text-gray-300 my-3"><span className="inline-flex items-center gap-1"><Gamepad2 size={16}/> Game Boy Advance</span><span className="inline-flex items-center gap-1"><CalendarDays size={16}/> {(selectedGame||FLASH_GAMES[1]).year}</span></div>
                <p className="text-gray-200 text-sm md:text-base mb-5 max-w-xl">{(selectedGame||FLASH_GAMES[1]).description}</p>
                <button className="dragon-games-play" onClick={()=>launchGame(selectedGame||FLASH_GAMES[1])}><Play size={21} fill="currentColor"/> PLAY GAME</button>
              </div>
            </div>
          )}
        </section>

        <section aria-label="Available games">
          <div className="dragon-games-section-heading flex items-center justify-between gap-3 mb-4">
            <h2 className="font-heading uppercase text-2xl md:text-4xl flex items-center gap-2"><Gamepad2 className="dragon-games-accent"/> Available Games ({filteredGames.length})</h2>
            <div className="flex gap-1" aria-label="Game display style">
              <button type="button" className={`dragon-games-view ${viewMode==='grid'?'active':''}`} onClick={()=>setViewMode('grid')} aria-label="Grid view" aria-pressed={viewMode==='grid'}><LayoutGrid size={19}/></button>
              <button type="button" className={`dragon-games-view ${viewMode==='list'?'active':''}`} onClick={()=>setViewMode('list')} aria-label="List view" aria-pressed={viewMode==='list'}><List size={19}/></button>
            </div>
          </div>
          <div className={viewMode==='grid'?'dragon-games-grid':'dragon-games-list'}>
            {filteredGames.map(game=><button key={game.id} type="button" onClick={()=>handleGameSelect(game)} className={`dragon-games-card ${selectedGame?.id===game.id?'selected':''}`} aria-label={`Select ${game.title}`}>
              <div className="dragon-games-card-cover"><img src={coverFor(game)} alt={`${game.title} original Game Boy Advance cover`} loading="lazy" /></div>
              <div className="dragon-games-card-details"><h3 className="dragon-games-card-title">{game.title}</h3><p>GBA · {game.year}</p></div>
              <span className="dragon-games-card-play" aria-hidden="true"><Play size={15} fill="currentColor"/></span>
            </button>)}
          </div>
        </section>
        <p className="dragon-games-notice"><Info size={17} className="shrink-0"/> Games are emulated in your browser. Select a game, press Play Game, then click inside the player and press Enter to start. Some games may take a moment to load.</p>
      </div>
    </main>
  );
};

export default GamesPage;
